import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import * as topoJson from 'topojson-client';
import * as topoServer from 'topojson-server';
import intersect from '@turf/intersect';
import cn from 'classnames';
import { geoPath, geoMercator } from 'd3-geo';
import { motion, AnimatePresence } from 'framer-motion';
import { groupBy, uniq } from 'lodash-es';
import { safeInvoke, Enum } from '../../../library';
import { IconArrowLeft } from '../../../icons';
import { Loader } from '../../loader';
import { Heading } from '../../type/heading';
import { Button, Buttons } from '../../button';
import { ResizeSensor } from '../../resize-sensor';
var SelectionStep = Enum({
  STATE_SELECT: 'state-select',
  REGION_SELECT: 'region-select',
  COUNTY_SELECT: 'county-select',
  FINISHED: 'finished'
});
var DEFAULT_TRANSITION = {
  type: 'tween',
  duration: .450,
  ease: 'easeInOut'
};
var DEFAULT_ZOOM = {
  scale: 1,
  translate: [0, 0],
  shouldAnimate: true
};
var StateContext = React.createContext({
  path: geoPath(geoMercator()),
  transition: DEFAULT_TRANSITION,
  zoom: DEFAULT_ZOOM
});
export var DevelopmentSelector = function DevelopmentSelector(props) {
  var _props$stateData$feat2, _props$stateData2, _props$stateData$feat3, _props$stateData3;
  var containerRef = React.useRef(null);
  var _React$useState = React.useState(SelectionStep.STATE_SELECT),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    currentStep = _React$useState2[0],
    setCurrentStep = _React$useState2[1];
  var _React$useState3 = React.useState(null),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    hoveredState = _React$useState4[0],
    setHoveredState = _React$useState4[1];
  var _React$useState5 = React.useState(null),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    selectedState = _React$useState6[0],
    setSelectedState = _React$useState6[1];
  var _React$useState7 = React.useState(null),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    selectedRegion = _React$useState8[0],
    setSelectedRegion = _React$useState8[1];
  var _React$useState9 = React.useState(null),
    _React$useState10 = _slicedToArray(_React$useState9, 2),
    selectedCounty = _React$useState10[0],
    setSelectedCounty = _React$useState10[1];
  var _React$useState11 = React.useState({
      width: 320,
      height: 640
    }),
    _React$useState12 = _slicedToArray(_React$useState11, 2),
    dimensions = _React$useState12[0],
    setDimensions = _React$useState12[1];
  var _React$useState13 = React.useState(false),
    _React$useState14 = _slicedToArray(_React$useState13, 2),
    isComplete = _React$useState14[0],
    setIsComplete = _React$useState14[1];
  var _React$useState15 = React.useState(DEFAULT_ZOOM),
    _React$useState16 = _slicedToArray(_React$useState15, 2),
    zoom = _React$useState16[0],
    setZoom = _React$useState16[1];
  var baseOutlines = React.useMemo(function () {
    if (!props.baseStateData) return {
      interior: null,
      exterior: null
    };
    var topoStates = topoServer.topology({
      collection: props.baseStateData
    });
    return {
      interior: topoJson.mesh(topoStates, topoStates.objects.collection, function (a, b) {
        return a !== b;
      }),
      exterior: topoJson.mesh(topoStates, topoStates.objects.collection, function (a, b) {
        return a === b;
      })
    };
  }, [props.baseStateData]);
  var enhancedRegions = React.useMemo(function () {
    if (!props.regionData || !props.countyData) return null;
    var regionCounties = groupBy(props.countyData.features, 'properties.regionId');
    var enhancedFeatures = props.regionData.features.map(function (feature) {
      var _regionCounties$map, _regionCounties;
      return _objectSpread(_objectSpread({}, feature), {}, {
        properties: _objectSpread(_objectSpread({}, feature.properties), {}, {
          states: uniq((_regionCounties$map = (_regionCounties = regionCounties[feature.id]) === null || _regionCounties === void 0 ? void 0 : _regionCounties.map(function (county) {
            return county.properties.stateSlug;
          })) !== null && _regionCounties$map !== void 0 ? _regionCounties$map : [])
        })
      });
    });
    return _objectSpread(_objectSpread({}, props.regionData), {}, {
      features: enhancedFeatures
    });
  }, [props.regionData, props.countyData]);
  var correctedRegionFeatures = React.useMemo(function () {
    if (!enhancedRegions) return null;
    return correctFeatures(enhancedRegions === null || enhancedRegions === void 0 ? void 0 : enhancedRegions.features);
  }, [enhancedRegions]);
  var correctedCountyFeatures = React.useMemo(function () {
    var _props$countyData;
    if (!props.countyData) return null;
    return correctFeatures((_props$countyData = props.countyData) === null || _props$countyData === void 0 ? void 0 : _props$countyData.features);
  }, [props.countyData]);
  var activeStates = React.useMemo(function () {
    var _props$stateData$feat, _props$stateData;
    return currentStep === SelectionStep.STATE_SELECT ? (_props$stateData$feat = (_props$stateData = props.stateData) === null || _props$stateData === void 0 ? void 0 : _props$stateData.features) !== null && _props$stateData$feat !== void 0 ? _props$stateData$feat : [] : selectedState ? [selectedState] : [];
  }, [currentStep, selectedState, props.stateData]);
  var activeRegions = React.useMemo(function () {
    if (!correctedRegionFeatures || !selectedState || currentStep !== SelectionStep.REGION_SELECT) return [];
    var filteredRegions = correctedRegionFeatures.filter(function (feature) {
      return feature.properties.states.includes(selectedState.properties.slug);
    });
    var clippedFeatures = filteredRegions.map(function (feature) {
      if (feature.properties.states.length > 1 && selectedState) {
        var clipped = intersect(selectedState, feature, {
          properties: feature.properties
        });
        if (clipped) {
          return correctFeature(clipped);
        }
        return feature;
      }
      return feature;
    });
    return clippedFeatures;
  }, [selectedState, correctedRegionFeatures, currentStep]);
  var activeCounties = React.useMemo(function () {
    if (!correctedCountyFeatures || !selectedRegion || !selectedState || currentStep !== SelectionStep.COUNTY_SELECT) return [];
    return correctedCountyFeatures.filter(function (feature) {
      return feature.properties.regionId === selectedRegion.properties.id && feature.properties.stateSlug === selectedState.properties.slug;
    });
  }, [selectedRegion, selectedState, correctedCountyFeatures, currentStep]);
  var path = React.useMemo(function () {
    var projection = geoMercator();
    if (props.stateData && props.stateData.features.length > 0) {
      projection.fitExtent([[60, dimensions.height * 0.25], [dimensions.width - 40, dimensions.height - 20]], props.stateData);
    }
    return geoPath(projection);
  }, [props.stateData, dimensions]);
  var transition = React.useMemo(function () {
    if (zoom.shouldAnimate) {
      return DEFAULT_TRANSITION;
    }
    return {
      duration: 0
    };
  }, [zoom.shouldAnimate]);
  React.useEffect(function () {
    var timeout;
    if (isComplete) {
      timeout = window.setTimeout(function () {
        safeInvoke(props.onComplete, {
          state: selectedState,
          region: selectedRegion,
          county: selectedCounty
        });
      }, 1250);
    }
    return function () {
      window.clearTimeout(timeout);
    };
  }, [isComplete, selectedState, selectedRegion, selectedCounty]);
  React.useEffect(function () {
    switch (currentStep) {
      case SelectionStep.STATE_SELECT:
        setZoom({
          scale: 1,
          translate: [0, 0],
          shouldAnimate: true
        });
        break;
      case SelectionStep.REGION_SELECT:
        if (selectedState) {
          boxZoom(path.bounds(selectedState), [160, 30, 60, 30], true);
        }
        break;
      case SelectionStep.COUNTY_SELECT:
        boxZoom(path.bounds({
          features: activeCounties,
          type: 'FeatureCollection'
        }), [160, 30, 60, 30], true);
        break;
      case SelectionStep.FINISHED:
        break;
    }
  }, [currentStep, selectedState, selectedRegion]);
  React.useEffect(function () {
    if (currentStep === SelectionStep.COUNTY_SELECT && activeCounties.length === 1) {
      handleCountyClick(activeCounties[0]);
    }
  }, [currentStep, activeCounties]);
  function boxZoom(bounds) {
    var padding = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [0, 0, 0, 0];
    var shouldAnimate = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var dx = bounds[1][0] - bounds[0][0];
    var dy = bounds[1][1] - bounds[0][1];
    var x = (bounds[0][0] + bounds[1][0]) / 2;
    var y = (bounds[0][1] + bounds[1][1]) / 2;
    var padY = padding[0] + padding[2];
    var padX = padding[1] + padding[3];
    var scale = Math.min((dimensions.width - padX) / dx, (dimensions.height - padY) / dy);
    var offsetX = (padding[3] - padding[1]) / 2;
    var offsetY = (padding[0] - padding[2]) / 2;
    var center = [dimensions.width / 2 + offsetX, dimensions.height / 2 + offsetY];
    var translate = [center[0] - scale * x, center[1] - scale * y];
    setZoom({
      scale: scale,
      translate: translate,
      shouldAnimate: shouldAnimate
    });
  }
  function handleNavigateBack(e) {
    e.preventDefault();
    switch (currentStep) {
      case SelectionStep.COUNTY_SELECT:
        setCurrentStep(SelectionStep.REGION_SELECT);
        break;
      case SelectionStep.REGION_SELECT:
        setCurrentStep(SelectionStep.STATE_SELECT);
        break;
    }
  }
  function handleStateClick(state) {
    if (currentStep !== SelectionStep.STATE_SELECT) return;
    setCurrentStep(SelectionStep.REGION_SELECT);
    setSelectedState(state !== null && state !== void 0 ? state : null);
    safeInvoke(props.onStateClick, state);
  }
  function handleStateMouseOut(state) {
    setHoveredState(null);
  }
  function handleStateMouseOver(state) {
    var _state$id;
    setHoveredState((_state$id = state === null || state === void 0 ? void 0 : state.id) !== null && _state$id !== void 0 ? _state$id : null);
  }
  function handleRegionClick(region) {
    setSelectedRegion(region);
    safeInvoke(props.onRegionClick, region);
    setCurrentStep(SelectionStep.COUNTY_SELECT);
  }
  function handleCountyClick(county) {
    setSelectedCounty(county);
    safeInvoke(props.onCountyClick, county);
    setIsComplete(true);
    setCurrentStep(SelectionStep.FINISHED);
  }
  function handleMapResize(sizes) {
    var _sizes$, _sizes$2;
    setDimensions({
      width: (_sizes$ = sizes[0]) === null || _sizes$ === void 0 ? void 0 : _sizes$.contentRect.width,
      height: (_sizes$2 = sizes[0]) === null || _sizes$2 === void 0 ? void 0 : _sizes$2.contentRect.height
    });
  }
  function renderBase() {
    var _path2, _path3;
    if (!props.baseStateData) return null;
    var activeStateAbrevs = props.stateData ? props.stateData.features.map(function (feature) {
      return feature.properties.abbreviation.toUpperCase();
    }) : [];
    return React.createElement(motion.g, {
      id: "base-map",
      initial: {
        strokeWidth: 1.5
      },
      animate: {
        strokeWidth: 1.5 / zoom.scale
      },
      transition: transition,
      width: dimensions.width,
      height: dimensions.height
    }, React.createElement("g", {
      id: "base-fill"
    }, props.baseStateData.features.map(function (feature) {
      var _path;
      return React.createElement("path", {
        key: feature.properties.STATE,
        className: cn('base-fill', {
          active: activeStateAbrevs.includes(feature.properties.ABBREV.toUpperCase())
        }),
        d: (_path = path === null || path === void 0 ? void 0 : path(feature)) !== null && _path !== void 0 ? _path : undefined
      });
    })), React.createElement("g", {
      id: "base-outlines"
    }, baseOutlines.interior && React.createElement("path", {
      className: "interior",
      d: (_path2 = path(baseOutlines.interior)) !== null && _path2 !== void 0 ? _path2 : undefined
    }), baseOutlines.exterior && React.createElement("path", {
      className: "exterior",
      d: (_path3 = path(baseOutlines.exterior)) !== null && _path3 !== void 0 ? _path3 : undefined
    })));
  }
  return React.createElement(StateContext.Provider, {
    value: {
      zoom: zoom,
      transition: transition,
      path: path
    }
  }, React.createElement(ResizeSensor, {
    onResize: handleMapResize
  }, React.createElement("div", {
    className: cn('development-selector', {
      '-loaded': !props.isLoading
    }, props.className),
    style: props.style,
    "data-step": currentStep
  }, React.createElement(Loader, {
    isLoading: props.isLoading,
    delay: 0
  }), React.createElement("div", {
    className: "development-selector__overlay"
  }, React.createElement(Header, {
    selectedCounty: selectedCounty,
    selectedRegion: selectedRegion,
    selectedState: selectedState,
    currentStep: currentStep
  }), React.createElement("footer", null, React.createElement(Buttons, {
    flexJustify: "center"
  }, React.createElement(Button, {
    className: "location-selector-back",
    appearance: "minimal",
    density: "compact",
    before: React.createElement(IconArrowLeft, null),
    onClick: handleNavigateBack,
    label: "Back"
  })), !!selectedCounty && currentStep === SelectionStep.FINISHED && React.createElement(Heading, {
    size: "sm",
    className: "t--uc"
  }, "Great! ", React.createElement("span", {
    className: "c--gray600"
  }, "Your Location Has Been Set.")))), React.createElement("div", {
    className: "development-selector__map-wrap"
  }, React.createElement(motion.div, {
    ref: containerRef,
    className: "development-selector__map",
    animate: {
      scale: zoom.scale,
      translateX: zoom.translate[0],
      translateY: zoom.translate[1]
    },
    transition: transition
  }, !props.isLoading && React.createElement(React.Fragment, null, React.createElement("div", {
    id: "region-labels"
  }, React.createElement(AnimatePresence, null, activeRegions.map(function (region) {
    return React.createElement(TextLabel, {
      key: region.properties.id,
      feature: region
    });
  }))), React.createElement("div", {
    id: "county-labels"
  }, React.createElement(AnimatePresence, null, activeCounties.map(function (region) {
    return React.createElement(TextLabel, {
      key: region.properties.id,
      feature: region
    });
  }))), React.createElement("div", {
    id: "state-labels"
  }, ((_props$stateData$feat2 = (_props$stateData2 = props.stateData) === null || _props$stateData2 === void 0 ? void 0 : _props$stateData2.features) !== null && _props$stateData$feat2 !== void 0 ? _props$stateData$feat2 : []).map(function (state) {
    return React.createElement(StateLabel, {
      key: state.properties.id,
      state: state,
      isActive: !!hoveredState && hoveredState === state.id
    });
  })), React.createElement(motion.div, {
    id: "state-abbrevs",
    animate: {
      fontSize: 14 / zoom.scale + 'px'
    },
    transition: transition
  }, ((_props$stateData$feat3 = (_props$stateData3 = props.stateData) === null || _props$stateData3 === void 0 ? void 0 : _props$stateData3.features) !== null && _props$stateData$feat3 !== void 0 ? _props$stateData$feat3 : []).map(function (state) {
    var _path$centroid = path.centroid(state),
      _path$centroid2 = _slicedToArray(_path$centroid, 2),
      x = _path$centroid2[0],
      y = _path$centroid2[1];
    return React.createElement("div", {
      key: state.properties.id,
      className: "state-abbrev",
      style: {
        top: y + 'px',
        left: x + 'px'
      }
    }, state.properties.abbreviation);
  })), React.createElement("svg", {
    viewBox: "0 0 ".concat(dimensions.width, " ").concat(dimensions.height),
    preserveAspectRatio: "xMidYMid meet",
    width: dimensions.width,
    height: dimensions.height
  }, React.createElement(motion.g, {
    initial: {
      strokeWidth: 1.5
    },
    animate: {
      strokeWidth: 1.5 / zoom.scale
    },
    transition: transition
  }, !!props.baseStateData && renderBase(), React.createElement(motion.g, {
    id: "state-fills",
    initial: {
      strokeWidth: 4
    },
    animate: {
      strokeWidth: 4 / zoom.scale
    },
    transition: transition
  }, activeStates.map(function (state) {
    return React.createElement(StateFill, {
      key: state.properties.id,
      onClick: function onClick() {
        return handleStateClick(state);
      },
      onMouseOver: function onMouseOver() {
        return handleStateMouseOver(state);
      },
      onMouseOut: function onMouseOut() {
        return handleStateMouseOut(state);
      },
      state: state
    });
  })), React.createElement(motion.g, {
    id: "region-fills",
    initial: {
      strokeWidth: 1.5,
      opacity: 0
    },
    animate: {
      strokeWidth: 1.5 / zoom.scale,
      opacity: 1
    },
    exit: {
      opacity: 0,
      transition: {
        opacity: {
          delay: 0
        }
      }
    },
    transition: transition,
    className: cn({
      active: currentStep === SelectionStep.REGION_SELECT
    })
  }, React.createElement(AnimatePresence, null, activeRegions.map(function (region) {
    return React.createElement(RegionFill, {
      key: region.properties.id,
      region: region,
      onClick: function onClick() {
        return handleRegionClick(region);
      }
    });
  }))), React.createElement(motion.g, {
    id: "county-fills",
    initial: {
      strokeWidth: 1.5
    },
    animate: {
      strokeWidth: 1.5 / zoom.scale
    },
    transition: transition,
    className: cn({
      active: currentStep === SelectionStep.COUNTY_SELECT
    })
  }, activeCounties.map(function (county) {
    return React.createElement(CountyFill, {
      key: county.properties.id,
      county: county,
      onClick: function onClick() {
        handleCountyClick(county);
      }
    });
  })), React.createElement(motion.g, {
    id: "state-outlines",
    initial: {
      strokeWidth: 3
    },
    animate: {
      strokeWidth: 3 / zoom.scale
    },
    transition: transition
  }, activeStates.map(function (state) {
    return React.createElement(StateOutline, {
      key: state.properties.id,
      state: state
    });
  }))))))))));
};
DevelopmentSelector.displayName = 'DevelopmentSelector';
DevelopmentSelector.defaultProps = {
  isLoading: false
};
function correctFeature(feature) {
  var coordinates = feature.geometry.coordinates;
  if (feature.geometry.type === 'Polygon') {
    coordinates = getCorrectedCoordinates(feature.geometry.coordinates);
  } else if (feature.geometry.type === 'MultiPolygon') {
    coordinates = getCorrectedCoordinates(feature.geometry.coordinates, true);
  }
  return _objectSpread(_objectSpread({}, feature), {}, {
    geometry: _objectSpread(_objectSpread({}, feature.geometry), {}, {
      coordinates: coordinates
    })
  });
}
function correctFeatures(features) {
  return features.map(correctFeature);
}
function getCorrectedCoordinates(coordinates) {
  var multi = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  if (multi) {
    return coordinates.map(function (moreCoordinates) {
      return getCorrectedCoordinates(moreCoordinates);
    });
  }
  return coordinates.map(function (path) {
    if (!isClockwisePath(path)) {
      return path.reverse();
    }
    return path;
  });
}
function isClockwisePath(coordinates) {
  if (coordinates.length < 3) return true;
  var current = coordinates[0];
  var result = coordinates.slice(1).reduce(function (total, next) {
    var b = next[0];
    var edge = (next[0] - current[0]) * (next[1] + current[1]);
    current = next;
    return total + edge;
  }, 0);
  return result > 0;
}
function Header(_ref) {
  var currentStep = _ref.currentStep,
    selectedCounty = _ref.selectedCounty,
    selectedRegion = _ref.selectedRegion,
    selectedState = _ref.selectedState;
  var stateTitleRef = React.useRef(null);
  var variants = {
    start: {
      opacity: 0,
      y: '100%'
    },
    active: {
      opacity: 1,
      y: '0%',
      transition: {
        delay: .15,
        duration: .35
      }
    },
    out: {
      opacity: 0,
      y: '-100%',
      transition: {
        duration: .35
      }
    }
  };
  React.useEffect(function () {
    if (stateTitleRef.current) {
      var children = stateTitleRef.current.children;
      var child = !!children ? children[(children === null || children === void 0 ? void 0 : children.length) - 1] : null;
      var height = !!child ? child.getBoundingClientRect().height : 0;
      stateTitleRef.current.style.height = height + 'px';
    }
  }, [currentStep, stateTitleRef]);
  return React.createElement("header", null, React.createElement(Heading, {
    size: 1,
    margin: [null, null, 2],
    className: "t--uc c--gray500"
  }, "Build Area"), React.createElement("div", {
    className: "state-titles",
    ref: stateTitleRef
  }, React.createElement(AnimatePresence, {
    initial: false
  }, React.createElement(motion.div, {
    key: currentStep,
    variants: variants,
    initial: 'start',
    exit: 'out',
    animate: 'active'
  }, React.createElement(Heading, {
    size: 6,
    family: "serif"
  }, currentStep === SelectionStep.STATE_SELECT && 'Select A State', currentStep === SelectionStep.REGION_SELECT && 'Select A Region', currentStep === SelectionStep.COUNTY_SELECT && 'Select A County', currentStep === SelectionStep.FINISHED && "".concat(selectedCounty === null || selectedCounty === void 0 ? void 0 : selectedCounty.properties.name, " County"))))), React.createElement(AnimatePresence, null, currentStep !== SelectionStep.REGION_SELECT ? null : React.createElement(motion.div, {
    className: "selected-state heading f--1",
    initial: {
      opacity: 0,
      y: '80%'
    },
    exit: {
      opacity: 0,
      y: '80%'
    },
    animate: {
      opacity: 1,
      y: '0%',
      transition: {
        delay: .35
      }
    },
    transition: {
      duration: .35
    }
  }, selectedState === null || selectedState === void 0 ? void 0 : selectedState.properties.name), currentStep !== SelectionStep.COUNTY_SELECT ? null : React.createElement(motion.div, {
    className: "selected-state heading f--1",
    initial: {
      opacity: 0,
      y: '80%'
    },
    exit: {
      opacity: 0,
      y: '80%'
    },
    animate: {
      opacity: 1,
      y: '0%',
      transition: {
        delay: .35
      }
    },
    transition: {
      duration: .35
    }
  }, selectedRegion === null || selectedRegion === void 0 ? void 0 : selectedRegion.properties.name)));
}
function StateFill(_ref2) {
  var _path4;
  var onClick = _ref2.onClick,
    onMouseOut = _ref2.onMouseOut,
    onMouseOver = _ref2.onMouseOver,
    state = _ref2.state;
  var _React$useContext = React.useContext(StateContext),
    path = _React$useContext.path;
  return React.createElement("path", {
    className: "state-fill",
    d: (_path4 = path(state)) !== null && _path4 !== void 0 ? _path4 : undefined,
    onClick: onClick,
    onMouseOver: onMouseOver,
    onMouseOut: onMouseOut
  });
}
function StateOutline(_ref3) {
  var _path5;
  var state = _ref3.state;
  var _React$useContext2 = React.useContext(StateContext),
    path = _React$useContext2.path;
  return React.createElement("path", {
    className: "state-outline",
    d: (_path5 = path(state)) !== null && _path5 !== void 0 ? _path5 : undefined
  });
}
function RegionFill(_ref4) {
  var _path6;
  var onClick = _ref4.onClick,
    region = _ref4.region;
  var _React$useContext3 = React.useContext(StateContext),
    path = _React$useContext3.path,
    transition = _React$useContext3.transition;
  return React.createElement(motion.path, {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0,
      transition: {
        opacity: {
          delay: 0
        }
      }
    },
    transition: transition,
    className: "region",
    d: (_path6 = path(region)) !== null && _path6 !== void 0 ? _path6 : undefined,
    onClick: onClick
  });
}
function CountyFill(_ref5) {
  var _path7;
  var county = _ref5.county,
    onClick = _ref5.onClick;
  var _React$useContext4 = React.useContext(StateContext),
    path = _React$useContext4.path;
  return React.createElement("path", {
    className: "county",
    d: (_path7 = path(county)) !== null && _path7 !== void 0 ? _path7 : undefined,
    onClick: onClick
  });
}
function StateLabel(props) {
  var _state$properties;
  var state = props.state;
  var _React$useContext5 = React.useContext(StateContext),
    path = _React$useContext5.path;
  var translate = path.centroid(state);
  return React.createElement("div", {
    className: cn('state-label', {
      active: props.isActive
    }),
    style: {
      top: translate[1] + 'px',
      left: translate[0] + 'px'
    }
  }, React.createElement("div", {
    className: "state-text"
  }, (_state$properties = state.properties) === null || _state$properties === void 0 ? void 0 : _state$properties.name));
}
;
function TextLabel(_ref6) {
  var _feature$properties$l;
  var feature = _ref6.feature;
  var _React$useContext6 = React.useContext(StateContext),
    zoom = _React$useContext6.zoom,
    transition = _React$useContext6.transition,
    path = _React$useContext6.path;
  var coords = path.centroid(feature);
  return React.createElement(motion.div, {
    key: feature.properties.id,
    className: "label",
    style: {
      top: coords === null || coords === void 0 ? void 0 : coords[1],
      left: coords === null || coords === void 0 ? void 0 : coords[0]
    },
    initial: {
      opacity: 0,
      scale: 1 / zoom.scale,
      x: '-50%',
      y: '-50%'
    },
    exit: {
      opacity: 0,
      transition: {
        opacity: {
          delay: 0
        }
      }
    },
    animate: {
      opacity: 1,
      scale: 1 / zoom.scale,
      x: '-50%',
      y: '-50%'
    },
    transition: transition
  }, (_feature$properties$l = feature.properties.label) !== null && _feature$properties$l !== void 0 ? _feature$properties$l : feature.properties.name);
}